// Reminder and update campaigns.
//
// Sending is batched and driven from here rather than fired once and forgotten: a thousand
// messages cannot go out inside one request, and batching means a closed browser loses
// nothing — each batch works from whatever is still unsent.

import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api.ts';
import {
  Notice, ErrorNotice, Loading, Field, TextInput, Select, TextArea, formatDate,
} from '../../components/Bits.tsx';
import { AdminChrome, useAdminSession } from './AdminChrome.tsx';

interface CampaignRow {
  id: number;
  subject: string;
  audience: string;
  cycle_year: number | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  total: number | string;
  sent: number | string;
}

interface CampaignsResponse {
  configured: boolean;
  host: string | null;
  separateProvider: boolean;
  ratePerHour: number;
  cycleYear: number;
  audiences: Record<string, string>;
  counts: Record<string, number>;
  campaigns: CampaignRow[];
}

interface Progress { sent: number; failed: number; remaining: number }

export function AdminCampaigns() {
  const { data: session, isPending } = useAdminSession();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState({ subject: '', body: '', audience: 'incomplete' });
  const [sending, setSending] = useState<number | null>(null);
  const [progress, setProgress] = useState<Progress | null>(null);

  const data = useQuery({
    queryKey: ['admin', 'campaigns'],
    queryFn: () => api.get<CampaignsResponse>('/api/admin/campaigns'),
    enabled: Boolean(session?.admin?.canConfigure),
  });

  const create = useMutation({
    mutationFn: () => api.post<{ campaign: CampaignRow }>('/api/admin/campaigns', draft),
    onSuccess: () => {
      setDraft({ subject: '', body: '', audience: 'incomplete' });
      queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
    },
  });

  /**
   * Send batch after batch until nothing is left.
   *
   * Driven from the browser so progress is visible and it can be stopped by closing the tab.
   * Nothing is lost if it is: each batch works from what is still unsent, so resuming is
   * simply clicking send again.
   */
  const sendAll = useMutation({
    mutationFn: async (id: number) => {
      setSending(id);
      setProgress(null);
      let total = { sent: 0, failed: 0, remaining: 0 };
      for (;;) {
        const batch = await api.post<Progress>(`/api/admin/campaigns/${id}/send`);
        total = {
          sent: total.sent + batch.sent,
          failed: total.failed + batch.failed,
          remaining: batch.remaining,
        };
        setProgress(total);
        if (batch.remaining === 0) break;
        // The server paces the sending itself, so this only avoids hammering the endpoint
        // between batches. A batch that sends nothing means the rate limit is holding it
        // back, so wait longer before asking again.
        await new Promise((r) => setTimeout(r, batch.sent === 0 ? 30_000 : 2_000));
      }
      return total;
    },
    onSettled: () => {
      setSending(null);
      queryClient.invalidateQueries({ queryKey: ['admin', 'campaigns'] });
    },
  });

  if (isPending) return <main id="main"><Loading /></main>;
  if (!session?.admin) return <Navigate to="/admin/signin" replace />;
  if (!session.admin.canConfigure) {
    return (
      <>
        <AdminChrome admin={session.admin} />
        <main id="main">
          <Notice kind="info">Sending email needs an administrator account.</Notice>
          <p><Link to="/admin">← Back to applications</Link></p>
        </main>
      </>
    );
  }

  const counts = data.data?.counts ?? {};
  const audienceCount = counts[draft.audience] ?? 0;

  return (
    <>
      <AdminChrome admin={session.admin} />
      <main id="main">
        <p><Link to="/admin">← Back to applications</Link></p>
        <h2>Email campaigns</h2>
        <p className="lede">
          Reminders to applicants who have not finished, and updates to those who asked for them.
        </p>

        <ErrorNotice error={data.error ?? create.error ?? sendAll.error} />

        {data.data && !data.data.configured && (
          <Notice kind="error">
            No mail server is configured, so campaigns cannot be sent. Set the seed-smtp-host,
            seed-smtp-user and seed-smtp-password secrets.
          </Notice>
        )}

        {data.data?.configured && (
          <Notice kind="info">
            Campaigns send through <strong>{data.data.host}</strong>
            {data.data.separateProvider
              ? ', a separate server from the transactional mail.'
              : ', the same server that sends password resets.'}
            {' '}Sending is paced at {data.data.ratePerHour} messages an hour
            {!data.data.separateProvider && (
              <> so a campaign cannot use up the quota that password resets depend on</>
            )}.
            {' '}A run of {counts[draft.audience] ?? 0} would take about{' '}
            {Math.max(1, Math.ceil((counts[draft.audience] ?? 0) / data.data.ratePerHour))} hour(s).
            You can close this page and resume later.
          </Notice>
        )}

        <h3>Write a campaign</h3>
        <form className="panel" onSubmit={(e) => { e.preventDefault(); create.mutate(); }}>
          <Field label="Who should receive this?">
            <Select
              value={draft.audience}
              onChange={(e) => setDraft({ ...draft, audience: e.target.value })}
            >
              {Object.entries(data.data?.audiences ?? {}).map(([key, label]) => (
                <option key={key} value={key}>
                  {label} — {counts[key] ?? 0}
                </option>
              ))}
            </Select>
          </Field>

          {draft.audience === 'opted-in' ? (
            <Notice kind="info">
              General updates go only to applicants who ticked the box asking for them. Nobody
              was carried over as opted in — there was never a box to tick before — so this
              audience grows only as people choose it.
            </Notice>
          ) : (
            <Notice kind="info">
              A message about someone's own application does not need prior consent, so this
              reaches everyone in that group for {data.data?.cycleYear}, minus anyone who has
              unsubscribed. Every message still carries an unsubscribe link.
            </Notice>
          )}

          <Field label="Subject">
            <TextInput
              value={draft.subject} required
              onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
            />
          </Field>

          <Field
            label="Message"
            hint="Write {{greeting}} where a personal greeting should go — it becomes “Hello Priya,” or “Hello,” if we have no name. A sign-off and an unsubscribe link are added automatically."
          >
            <TextArea
              value={draft.body} required style={{ minHeight: '13rem' }}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />
          </Field>

          <div className="actions">
            <button type="submit" disabled={create.isPending || audienceCount === 0}>
              {create.isPending ? 'Preparing…' : `Prepare for ${audienceCount} recipients`}
            </button>
            <span className="hint">
              This writes the recipient list and sends nothing. You send it below.
            </span>
          </div>
        </form>

        <h3>Campaigns</h3>
        {data.isPending ? <Loading /> : (data.data?.campaigns.length ?? 0) === 0 ? (
          <p className="empty">None yet.</p>
        ) : (
          <div className="panel scroll-x">
            <table>
              <thead>
                <tr>
                  <th>Subject</th><th>Audience</th><th className="num">Progress</th>
                  <th>Created</th><th />
                </tr>
              </thead>
              <tbody>
                {(data.data?.campaigns ?? []).map((c) => {
                  const total = Number(c.total);
                  const sent = Number(c.sent);
                  const done = Boolean(c.finished_at);
                  return (
                    <tr key={c.id}>
                      <td>{c.subject}</td>
                      <td>
                        {c.audience}
                        {c.cycle_year && <span className="tag"> {c.cycle_year}</span>}
                      </td>
                      <td className="num">
                        {sent} of {total}
                        {done && <> <span className="tag open">done</span></>}
                      </td>
                      <td>{formatDate(c.created_at)}</td>
                      <td>
                        {!done && (
                          <button
                            className="quiet"
                            disabled={sending !== null || !data.data?.configured}
                            onClick={() => {
                              if (window.confirm(
                                `Send "${c.subject}" to ${total - sent} recipients? This cannot be undone.`,
                              )) sendAll.mutate(c.id);
                            }}
                          >
                            {sending === c.id ? 'Sending…' : sent > 0 ? 'Resume' : 'Send'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {progress && (
          <Notice kind={progress.remaining === 0 ? 'ok' : 'info'}>
            {progress.remaining === 0
              ? `Finished. ${progress.sent} sent${progress.failed ? `, ${progress.failed} failed` : ''}.`
              : `Sending… ${progress.sent} sent, ${progress.remaining} to go. Leave this page open,
                 or close it and click Resume later — nothing will be sent twice.`}
          </Notice>
        )}
      </main>
    </>
  );
}
